import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { CellularDeviceCommunicationHardwareService } from '../cellular-device/cellular-device-communication-hardware.service';
import { Utilities } from '../utilities/Utilities';
import { CellularDeviceInfo } from '../../src/typings/api';
import { CellularDeviceService } from '../cellular-device/cellular-device.service';
import { CellularCarrierService } from '../cellular-device/cellular-carrier.service';
import { CellularDeviceAssignmentService } from '../cellular-device/cellular-device-assignment.service';
import { Subscription } from 'rxjs';
import { CommonBroadcastService } from '../common-broadcast/common-broadcast.service';
import { EventTypes } from '@caslink/common-client';

@Component({
    selector: 'cl-cellular-device-selector',
    templateUrl: './cellular-device-selector.component.html',
    styleUrls: ['./cellular-device-selector.component.scss']
})
export class CellularDeviceSelectorComponent {
    static $name: string = 'clCellularDeviceSelector';

    /** The passed-in object of existing values & output object for changed values */
    @Input() cellularDeviceInfo: CellularDeviceInfo;
    /** This must be true for the 'Request Activation' button to appear at all */
    @Input() canRequestActivation: boolean;
    /** This must be true for the 'Update Cellular Device Status' button to be present at all */
    @Input() canRequestActivationStatus: boolean;
    @Input() canRequestDeactivation: boolean;
    /** This must be true to enable all Cellular Device controls */
    @Input() canEditCellularDevice: boolean = false;
    @Input() jobNumber: number;
    @Input() locationId: number;
    @Input() moduleId: number;
    @Input() mustSaveBeforeActivation: boolean = false;
    @Input() cellularDeviceStatus: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceStatusViewModel;

    @Output() onUpdateCellularDeviceStatus = new EventEmitter<CASLink.Device.Ui.ViewModel.Modules.CellularDeviceStatusViewModel>();
    @Output() deviceSelectValidChange = new EventEmitter<null>();
    @Output() deviceSelectInvalidChange = new EventEmitter<{touched: boolean}>();

    /** This is a list of ALL possible device assignments, it comes directly from the server */
    assignments: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel[];
    /** This is a list of ALL possible Communication Hardware, it comes directly from the server */
    communicationHardware: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceCommunicationHardwareViewModel[];
    /** This is a list of ALL possible Carriers, it comes directly from the server */
    carriers: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[];
    /** This is a list of the carriers that are available for the currently selected device type */
    currentCarriers: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[];
    /** This is a list of the device types that are available for the currently selected communication hardware */
    currentDeviceTypes: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[];
    /** A list of ALL possible devices, coming from the server */
    devices: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[];

    /** Single source of knowledge for the placeholder when no carrier is selected */
    noAssignment: string = 'None';
    noCarrier: string = 'None';
    noDevice: string = 'None';
    noHardware: string = 'None';

    selectedCommunicationHardwareName = 'selectedCommunicationHardware';
    selectedDeviceTypeName = 'selectedDeviceType';
    selectedCellularCarrierName = 'selectedCellularCarrier';
    cellularDeviceInfoIdentifiersName = 'mobileCellularDeviceIdentifiers';
    selectedCellularDeviceAssignmentName = 'selectedCellularDeviceAssignment';
    selectedCellularVendorName = 'selectedCellularVendor';

    cellularDeviceForm = new FormGroup({
        [this.selectedCommunicationHardwareName]: new FormControl('', [Validators.required]),
        [this.selectedDeviceTypeName]: new FormControl(''),
        [this.selectedCellularCarrierName]: new FormControl('', [Validators.required]),
        [this.cellularDeviceInfoIdentifiersName]: new FormArray([]),
        [this.selectedCellularDeviceAssignmentName]: new FormControl('', [Validators.required]),
        [this.selectedCellularVendorName]: new FormControl('', [Validators.required])
    });

    private decRegex = /^\d*$/;
    private hexRegex = /^[\d a-f A-F]*$/;
    private formSubscription = new Subscription();
    private undoSubscription = new Subscription();

    get cellularDeviceInfoIdentifiersNameArray() {
        return this.cellularDeviceForm.get(this.cellularDeviceInfoIdentifiersName) as FormArray;
    }

    get selectedCellularCarrierControl() {
        return this.cellularDeviceForm.get(this.selectedCellularCarrierName);
    }

    get selectedDeviceTypeControl() {
        return this.cellularDeviceForm.get(this.selectedDeviceTypeName);
    }

    get selectedCommunicationHardwareControl() {
        return this.cellularDeviceForm.get(this.selectedCommunicationHardwareName);
    }

    get selectedCellularDeviceAssignmentControl() {
        return this.cellularDeviceForm.get(this.selectedCellularDeviceAssignmentName);
    }

    get selectedCellularVendorControl() {
        return this.cellularDeviceForm.get(this.selectedCellularVendorName);
    }

    get showActivation() {
        if (this.mustSaveBeforeActivation) {
            return this.cellularDeviceForm?.pristine;
        }

        return this.cellularDeviceForm?.disabled || this.cellularDeviceForm?.valid;
    }

    get canUpdateCellularDeviceStatus() {
        return this.canRequestActivationStatus && this.selectedCellularCarrierControl.value?.vendors?.length;
    }

    constructor(
        private cellularCarrierService: CellularCarrierService,
        private cellularDeviceAssignmentService: CellularDeviceAssignmentService,
        private cellularDeviceCommunicationHardwareService: CellularDeviceCommunicationHardwareService,
        private cellularDeviceService: CellularDeviceService,
        private commonBroadcastService: CommonBroadcastService,
        private utilities: Utilities,
    ) {
    }

    ngOnInit() {
        this.initialize();
    }

    ngOnDestroy() {
        this.formSubscription.unsubscribe();
        this.undoSubscription.unsubscribe();
    }

    private formValidationSubscriptionForOldNg() {
        if (this.cellularDeviceForm.invalid) { this.deviceSelectInvalidChange.emit();}
        this.formSubscription = this.cellularDeviceForm.valueChanges.subscribe(() => {
            this.emitCrossNgValidation();
        });
    }

    private emitCrossNgValidation() {
        if (this.cellularDeviceForm.valid) {
            this.deviceSelectValidChange.emit();
        } else {
            this.deviceSelectInvalidChange.emit({touched: true});
        }
    }

    private initialize(): void {
        Promise.all([
            this.getCellularDeviceCommunicationHardware(),
            this.getCellularDevices(),
            this.getCellularCarriers(),
            this.getCellularDeviceAssignments()
        ]).then(() => {
            this.populateCellularDeviceInfo();
            this.initializeDisabledFormProperties();

        }).then(() => {
            this.cellularDeviceForm.markAllAsTouched();
            this.formValidationSubscriptionForOldNg();
            this.onUndoSubscription();
        });
    }

    private initializeDisabledFormProperties() {
        this.toggleDisabledInputConditionally(this.selectedCommunicationHardwareName);
        this.toggleDisabledInputConditionally(this.selectedDeviceTypeName, this.currentDeviceTypes?.length ?? 0);
        this.toggleDisabledInputConditionally(this.selectedCellularCarrierName, this.currentCarriers?.length ?? 0);
        this.toggleDisabledInputConditionally(this.cellularDeviceInfoIdentifiersName);
        this.toggleDisabledInputConditionally(this.selectedCellularDeviceAssignmentName);
        this.toggleDisabledInputConditionally(this.selectedCellularVendorName);
    }

    private toggleDisabledInputConditionally(inputFormName: string, selectInputLength?: number) {
        if (selectInputLength === 1 || !this.canEditCellularDevice) {
            this.cellularDeviceForm.get(inputFormName).disable();
        } else {
            this.cellularDeviceForm.get(inputFormName).enable();
        }
    }

    private filterCarriers(): CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[] {
        const deviceType = this.selectedDeviceTypeControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel;
        if (!deviceType) {
            return [];
        }

        return this.carriers.filter((carrier: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel) => {
            return deviceType.cellularCarriers
                .some((c: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel) => c.id === carrier.id);
        });
    }

    private filterDeviceTypes(): CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[] {
        const communicationHardware = this.selectedCommunicationHardwareControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularDeviceCommunicationHardwareViewModel;
        if (!communicationHardware) {
            return [];
        }

        return this.devices.filter(device => communicationHardware.cellularDeviceTypes.some(d => d.id === device.id));
    }

    private getCellularCarriers(): angular.IPromise<CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[]> {
        return this.cellularCarrierService.getCellularCarriers()
            .then((carriers: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[]) => {
                return this.carriers = carriers;
            });
    }

    private getCellularDeviceAssignments(): angular.IPromise<CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel[]> {
        return this.cellularDeviceAssignmentService.getCellularDeviceAssignments()
            .then((assignments: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel[]) => {
                return this.assignments = assignments;
            });
    }

    private getCellularDeviceCommunicationHardware(): Promise<CASLink.Device.Ui.ViewModel.Modules.CellularDeviceCommunicationHardwareViewModel[]> {
        return this.cellularDeviceCommunicationHardwareService.getCommunicationHardware().then(hardware => {
            return this.communicationHardware = hardware;
        });
    }

    private getCellularDevices(): angular.IPromise<CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[]> {
        return this.cellularDeviceService.getCellularDevices()
            .then((devices: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[]) => {
                return this.devices = devices;
            });
    }

    private populateCellularDeviceInfo() {
        /* Prepopulate the info if it is passed in to start */
        if (this.cellularDeviceInfo) {
            this.preselectCellularDeviceCommunicationHardware(this.cellularDeviceInfo.cellularDeviceCommunicationHardwareId);
            this.preselectCellularDeviceType(this.cellularDeviceInfo.cellularDeviceTypeId);
            this.preselectCellularDeviceAssignment(this.cellularDeviceInfo.cellularDeviceAssignmentId);
            this.currentCarriers = this.filterCarriers();
            this.currentDeviceTypes = this.filterDeviceTypes();
            this.preselectCarrier(this.cellularDeviceInfo.cellularCarrierId);
            this.preselectVendor(this.cellularDeviceInfo.cellularVendorId);

        } else {
            /**
             * This whole else clause is for handling the case where the entity information
             * was NOT prepopulated, and we press 'Undo' after changing it
             * Without wrapping this in an $applyAsync,
             * you will see two distinct cycles happen where the carrier is cleared, then the deviceId is removed
             */
            this.selectedCellularDeviceAssignmentControl.setValue(null);
            this.selectedCellularCarrierControl.setValue(null);

            /**
             *  If not we want to populate the object with garbage data so our custom watcher
             * will actually get hit when the Undo button is clicked :)
             */
            this.cellularDeviceInfo = {
                cellularCarrierId: null,
                cellularCarrierName: this.noCarrier,
                cellularDeviceAssignmentId: null,
                cellularDeviceAssignmentName: this.noAssignment,
                cellularDeviceCommunicationHardwareId: null,
                cellularDeviceCommunicationHardwareName: this.noHardware,
                cellularDeviceInfoIdentifiers: null,
                cellularDeviceTypeId: null,
                cellularDeviceTypeName: this.noDevice,
                cellularVendorId: null,
                moduleId: this.moduleId,
                dataPlanExpirationVisible: false,
                isFreeSubscription: false,
                isFreeSubscriptionVisible: false,
                allowedMobileIdTypes: null,
                cellularVendorName: null,
                dataPlanExpiration: null,
                updatedCellularDeviceInfoIdentifiers: null
            } as CellularDeviceInfo;
            this.utilities.addGarbageData(this.cellularDeviceInfo);
        }
    }


    private populateIdentifiers(identifiers: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceInfoIdentifierViewModel[]) {
        const cellularCarrier = this.selectedCellularCarrierControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel;

        if (!this.cellularDeviceInfo.updatedCellularDeviceInfoIdentifiers) {
            this.cellularDeviceInfo.updatedCellularDeviceInfoIdentifiers = {};
        }

        if (cellularCarrier && identifiers) {
            identifiers.forEach((identifier: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceInfoIdentifierViewModel) => {
                this.cellularDeviceInfo.updatedCellularDeviceInfoIdentifiers[identifier.mobileIdTypeId] = identifier.deviceId;
            });
        }
    }

    private preselectCellularDeviceCommunicationHardware(cellularDeviceCommunicationHardwareId: string) {
        const filteredHardware = this.communicationHardware.filter(hardware => hardware.id === cellularDeviceCommunicationHardwareId);
        this.selectedCommunicationHardwareControl.setValue(filteredHardware.length ? filteredHardware[0] : null);

        this.updateCellularDeviceCommunicationHardware();
    }

    private preselectCellularDeviceType(cellularDeviceTypeId: string) {
        let filteredDeviceTypes: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[];
        /** If there's only one device type, nothing else matters. Select it. */
        if (this.currentDeviceTypes.length === 1) {
            filteredDeviceTypes = [this.currentDeviceTypes[0]];
        } else {
            filteredDeviceTypes =
                this.currentDeviceTypes.filter((device: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel) => {
                    return cellularDeviceTypeId === device.id;
                });
        }

        /**
         * The potential null assignment handles this case:
         * 1. The component was entered with no cellularDeviceInfo & this function populates it with the noDevice name & garbage data
         * 2. The component was destroyed (*ngIf)
         * 3. The component is recreated -- we'll now be in this block with no matching devices
         */
        this.selectedDeviceTypeControl.setValue(filteredDeviceTypes.length ? filteredDeviceTypes[0] : null);

        this.updateCellularDeviceType();
    }

    private preselectCellularDeviceAssignment(cellularDeviceAssignmentId: string) {
        let filteredAssignments: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel[] =
            this.assignments.filter((assignment: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel) => {
                return cellularDeviceAssignmentId === assignment.id;
            });

        this.selectedCellularDeviceAssignmentControl.setValue(filteredAssignments.length ? filteredAssignments[0] : null);
        this.updateCellularDeviceAssignment();
    }

    private preselectCarrier(cellularCarrierId: string) {
        let filteredCarriers: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[];
        /* If there's only one carrier, nothing else matters. Select it. */
        if (this.currentCarriers.length === 1) {
            filteredCarriers = [this.currentCarriers[0]];
        } else {
            filteredCarriers = this.currentCarriers.filter((carrier: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel) => {
                return cellularCarrierId === carrier.id;
            });
        }

        /**
         * The potential null assignment handles this case:
         * 1. The component was entered with no cellularDeviceInfo & this function populates it with the noCarrier name & garbage data
         * 2. The component was destroyed (*ngIf)
         * 3. The component is recreated -- we'll now be in this block with no matching carriers
         */
        this.selectedCellularCarrierControl.setValue(filteredCarriers.length ? filteredCarriers[0] : null);

        // we can populate identifiers once we preselected carrier
        this.populateIdentifiers(this.cellularDeviceInfo.cellularDeviceInfoIdentifiers);
        this.updateCellularCarrier();
    }

    private preselectVendor(cellularVendorId: string) {
        let filteredVendors: CASLink.Device.Ui.ViewModel.Modules.CellularVendorViewModel[];
        const cellularCarrier = this.selectedCellularCarrierControl.value;
        const vendorControl = this.selectedCellularVendorControl;
        if (!cellularCarrier) {
            return null;
        }

        /* If there's only one vendor, nothing else matters. Select it. */
        // tslint:disable-next-line: prefer-conditional-expression
        if (cellularCarrier.vendors.length === 1) {
            filteredVendors = [cellularCarrier.vendors[0]];
        } else {
            filteredVendors = cellularCarrier.vendors.filter(vendor => vendor.id === cellularVendorId);
        }

        vendorControl.setValue(filteredVendors.length ? filteredVendors[0] : null);

        this.updateVendor();
    }


    updateCellularDeviceAssignment() {
        const assignment = this.selectedCellularDeviceAssignmentControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel;

        this.cellularDeviceInfo.cellularDeviceAssignmentId = assignment ? assignment.id : null;
        this.cellularDeviceInfo.cellularDeviceAssignmentName = assignment ? assignment.name : this.noAssignment;
    }

    updateCellularDeviceCommunicationHardware() {
        const communicationHardware = this.selectedCommunicationHardwareControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularDeviceCommunicationHardwareViewModel;
        this.cellularDeviceInfo.cellularDeviceCommunicationHardwareId = communicationHardware ? communicationHardware.id : null;

        this.cellularDeviceInfo.cellularDeviceCommunicationHardwareName = communicationHardware ? communicationHardware.name : this.noHardware;

        this.currentDeviceTypes = this.filterDeviceTypes();
        this.preselectCellularDeviceType(this.cellularDeviceInfo.cellularDeviceTypeId);

        this.updateCellularDeviceType();
    }

    updateCellularDeviceType() {
        const deviceType = this.selectedDeviceTypeControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularDeviceCommunicationHardwareViewModel;

        this.cellularDeviceInfo.cellularDeviceTypeId = deviceType ? deviceType.id : null;
        this.cellularDeviceInfo.cellularDeviceTypeName = deviceType ? deviceType.name : this.noDevice;
        this.toggleDisabledInputConditionally(this.selectedDeviceTypeName, this.currentDeviceTypes.length);

        this.currentCarriers = this.filterCarriers();
        this.preselectCarrier(this.cellularDeviceInfo.cellularCarrierId);

        this.updateCellularCarrier();
    }

    updateCellularCarrier() {
        const cellularCarrier = this.selectedCellularCarrierControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel;

        this.cellularDeviceInfo.cellularCarrierId = cellularCarrier ? cellularCarrier.id : null;
        this.cellularDeviceInfo.cellularCarrierName = cellularCarrier ? cellularCarrier.name : this.noCarrier;
        this.cellularDeviceInfo.allowedMobileIdTypes = cellularCarrier ? cellularCarrier.mobileIdTypes : [];
        this.toggleDisabledInputConditionally(this.selectedCellularCarrierName, this.currentCarriers.length);

        this.updateCellularIdentifiers();
        this.preselectVendor(this.cellularDeviceInfo.cellularVendorId);
    }

    updateCellularIdentifiers() {
        const cellularCarrier = this.selectedCellularCarrierControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel;
        const mobileIdTypesFormArray = this.cellularDeviceInfoIdentifiersNameArray;

        mobileIdTypesFormArray.clear();

        if (cellularCarrier) {
            cellularCarrier.mobileIdTypes.forEach((mobileIdType: CASLink.Device.Ui.ViewModel.Modules.MobileIdTypeViewModel) => {
                const validators: ValidatorFn[] = this.getIdentifierValidators(mobileIdType, cellularCarrier);
                mobileIdTypesFormArray.push(new FormControl(this.cellularDeviceInfo.updatedCellularDeviceInfoIdentifiers[mobileIdType.id], validators));
            });
        }
    }

    getIdentifierValidators(identifier: CASLink.Device.Ui.ViewModel.Modules.MobileIdTypeViewModel, cellularCarrier: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel): ValidatorFn[] {
        let currentMobileIdType = cellularCarrier.mobileIdTypes.find(mobileType => mobileType.id === identifier.id);
        const hexBase = 16;
        let isHex = currentMobileIdType.base === hexBase;
        let regex = isHex ? this.hexRegex : this.decRegex;

        return [Validators.minLength(currentMobileIdType.minChars), Validators.maxLength(currentMobileIdType.maxChars), Validators.pattern(regex), Validators.required];
    }

    updateCellularIdentifier(index: number) {
        const cellularCarrier = this.selectedCellularCarrierControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel;
        const mobileIdTypesFormArray = this.cellularDeviceInfoIdentifiersNameArray;

        const updatedId = cellularCarrier.mobileIdTypes[index].id;
        this.cellularDeviceInfo.updatedCellularDeviceInfoIdentifiers[updatedId] = mobileIdTypesFormArray.at(index).valid ? mobileIdTypesFormArray.at(index).value : undefined;
    }

    updateVendor() {
        const vendor = this.selectedCellularVendorControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularVendorViewModel;

        this.cellularDeviceInfo.cellularVendorId = vendor ? vendor.id : null;
        this.cellularDeviceInfo.cellularVendorName = vendor ? vendor.displayName : null;
        this.cellularDeviceForm.markAllAsTouched();
    }

    getValidationText(validationKey: string, index: number) {
        const cellularCarrier = this.selectedCellularCarrierControl.value as CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel;
        const mobileIdType = cellularCarrier.mobileIdTypes[index];
        switch (validationKey) {
            case 'pattern':
                const hexBase = 16;
                let isHex = mobileIdType.base === hexBase;

                return `Characters allowed: ${isHex ? '(0-9) (A-F)' : '(0-9)'}`;
            case 'maxlength':
            case 'minlength':
                if (mobileIdType.maxChars === mobileIdType.minChars) { return `Must have length of ${mobileIdType.maxChars} characters`; }
                return `Must have length between ${mobileIdType.minChars} and ${mobileIdType.maxChars} characters`;
        }

    }

    onCellularDeviceStatusChanged(cellularDeviceStatus: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceStatusViewModel) {
        this.onUpdateCellularDeviceStatus.emit(cellularDeviceStatus);
    }

    onUndoSubscription() {
        this.undoSubscription = this.commonBroadcastService.on(EventTypes.SelectionUndo)
            .subscribe(() => {
                // set timeout because race condition between data change in parent and populating this component
                setTimeout(() => {
                    // we need to unsubscribe and subscribe again for avoiding situation where we fire cross angular/oldng validation when populating on undo...
                    this.formSubscription.unsubscribe();
                    this.populateCellularDeviceInfo();
                    this.cellularDeviceForm.markAsPristine();
                    this.formValidationSubscriptionForOldNg();
                });
            });
    }
}
